import type { ReactNode } from 'react';
import type { BreakdownSubItem, BreakdownSubItemInput } from '@jobos/shared';
import { offlineCarpetKey, offlineEquipmentKey } from '@/hooks/useOfflineStorage';
import { useCarpetCatalog } from '@/hooks/useCarpetCatalog';
import { useEquipmentCatalog } from '@/hooks/useEquipmentCatalog';
import { useEnsureCarpetLineItem } from '@/hooks/useCarpetLineItem';
import { useEnsureEquipmentLineItem } from '@/hooks/useEquipmentLineItem';
import { CarpetEditor } from '../carpet/CarpetEditor';
import { EquipmentEditor } from '../equipment/EquipmentEditor';
import type { EnsureLineItem } from './useSingleCategorySave';

export interface EditorRenderProps {
  initialSubItems: BreakdownSubItem[];
  onChange: (next: BreakdownSubItemInput[]) => void;
  readOnly: boolean;
}

export interface SingleCategoryConfig {
  category: string;
  backupKind: 'carpet' | 'equipment';
  offlineKey: (jobId: string) => string;
  defaultProjectType: string;
  /** Human-readable noun spliced into the catalog-load failure copy. */
  catalogErrorLabel: string;
  // Hooks called unconditionally inside the view; the dispatcher keys the view
  // on category so hook order stays stable across config swaps.
  useCatalog: () => { isLoading: boolean; isError: boolean; data: unknown };
  useEnsureLineItem: () => EnsureLineItem & { isPending: boolean };
  generate: { path: (jobId: string) => string; filename: (jobCode: string) => string };
  renderEditor: (catalog: any, props: EditorRenderProps) => ReactNode;
}

export const CARPET_CONFIG: SingleCategoryConfig = {
  category: 'Carpet Cleaning',
  backupKind: 'carpet',
  offlineKey: offlineCarpetKey,
  defaultProjectType: 'Carpet Cleaning',
  catalogErrorLabel: 'carpet catalog',
  useCatalog: useCarpetCatalog,
  useEnsureLineItem: useEnsureCarpetLineItem,
  generate: {
    path: (jobId) => `/jobs/${jobId}/carpet-tracker`,
    filename: (jobCode) => `${jobCode}-carpet-tracker.xlsx`,
  },
  renderEditor: (catalog, p) => <CarpetEditor catalog={catalog} {...p} />,
};

export const EQUIPMENT_CONFIG: SingleCategoryConfig = {
  category: 'Equipment Rental',
  backupKind: 'equipment',
  offlineKey: offlineEquipmentKey,
  defaultProjectType: 'Equipment Rental',
  catalogErrorLabel: 'equipment catalog',
  useCatalog: useEquipmentCatalog,
  useEnsureLineItem: useEnsureEquipmentLineItem,
  generate: {
    path: (jobId) => `/jobs/${jobId}/equipment-bill`,
    filename: (jobCode) => `${jobCode}-equipment-bill.xlsx`,
  },
  renderEditor: (catalog, p) => (
    <EquipmentEditor equipmentTypes={catalog.types} serviceRates={catalog.serviceRates} {...p} />
  ),
};
